import { useState, useMemo } from 'react';
import { Search, Download, FileText, Info, ExternalLink, Moon, Sun } from 'lucide-react';

const DevOpsToolsDocumentation = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isDarkMode, setIsDarkMode] = useState(true);

  const tools = [
    {
      name: 'Git',
      category: 'Version Control',
      description: 'Distributed version control system - absolutely essential for all DevOps work',
      requirements: 'Minimal system requirements, varies by platform',
      downloadUrl: 'https://git-scm.com/downloads',
      docsUrl: 'https://git-scm.com/book/en/v2/Getting-Started-Installing-Git',
      version: 'Latest',
      platforms: ['Windows', 'macOS', 'Linux', 'Unix'],
      priority: 'Critical',
      logoUrl: 'https://git-scm.com/images/logos/downloads/Git-Logo-2Color.png'
    },
    {
      name: 'Docker',
      category: 'Containerization',
      description: 'Platform for developing, shipping, and running applications in containers - fundamental for modern DevOps',
      requirements: '4GB RAM, 64-bit processor, Hyper-V (Windows)',
      downloadUrl: 'https://www.docker.com/products/docker-desktop/',
      docsUrl: 'https://docs.docker.com/get-docker/',
      version: 'Desktop/Engine',
      platforms: ['Windows', 'macOS', 'Linux'],
      priority: 'Critical',
      logoUrl: 'https://www.docker.com/wp-content/uploads/2022/03/horizontal-logo-monochromatic-white.png'
    },
    {
      name: 'Kubernetes',
      category: 'Container Orchestration',
      description: 'Container orchestration platform - industry standard for production deployments',
      requirements: '2GB RAM, 2 CPUs minimum per node',
      downloadUrl: 'https://kubernetes.io/docs/tasks/tools/',
      docsUrl: 'https://kubernetes.io/docs/setup/',
      version: 'Latest stable',
      platforms: ['Windows', 'macOS', 'Linux'],
      priority: 'Critical',
      logoUrl: 'https://kubernetes.io/images/kubernetes-horizontal-color.png'
    },
    {
      name: 'AWS CLI v2',
      category: 'Cloud Tools',
      description: 'Command line interface for Amazon Web Services - essential for cloud operations',
      requirements: 'Python 3.7+, 64-bit version of Windows/macOS/Linux',
      downloadUrl: 'https://aws.amazon.com/cli/',
      docsUrl: 'https://docs.aws.amazon.com/cli/latest/userguide/getting-started-install.html',
      version: 'v2.x',
      platforms: ['Windows', 'macOS', 'Linux'],
      priority: 'Critical',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg'
    },
    {
      name: 'Terraform',
      category: 'Infrastructure as Code',
      description: 'Infrastructure automation tool - standard for IaC in cloud environments',
      requirements: '64-bit processor, varies by platform',
      downloadUrl: 'https://www.terraform.io/downloads',
      docsUrl: 'https://learn.hashicorp.com/tutorials/terraform/install-cli',
      version: 'Latest',
      platforms: ['Windows', 'macOS', 'Linux', 'FreeBSD'],
      priority: 'Critical',
      logoUrl: 'https://www.terraform.io/img/logo-hashicorp.svg'
    },
    {
      name: 'Python 3.12',
      category: 'Programming Language',
      description: 'High-level programming language - essential for automation, scripting, and DevOps tools',
      requirements: 'Varies by platform, typically 25MB disk space',
      downloadUrl: 'https://www.python.org/downloads/',
      docsUrl: 'https://docs.python.org/3/using/index.html',
      version: '3.12.x',
      platforms: ['Windows', 'macOS', 'Linux', 'Source'],
      priority: 'Critical',
      logoUrl: 'https://www.python.org/static/img/python-logo.png'
    },
    {
      name: 'Visual Studio Code',
      category: 'IDE/Editor',
      description: 'Lightweight but powerful source code editor - primary development environment',
      requirements: '1.6GHz+ processor, 1GB RAM, 200MB disk space',
      downloadUrl: 'https://code.visualstudio.com/download',
      docsUrl: 'https://code.visualstudio.com/docs/setup/setup-overview',
      version: 'Latest',
      platforms: ['Windows', 'macOS', 'Linux'],
      priority: 'Critical',
      logoUrl: 'https://code.visualstudio.com/assets/images/code-stable.png'
    },
    {
      name: 'Jenkins',
      category: 'CI/CD',
      description: 'Open source automation server - industry standard for CI/CD pipelines',
      requirements: 'Java 8 or 11, 256MB RAM minimum, 1GB+ disk space',
      downloadUrl: 'https://www.jenkins.io/download/',
      docsUrl: 'https://www.jenkins.io/doc/book/installing/',
      version: 'Latest LTS',
      platforms: ['Windows', 'macOS', 'Linux', 'Docker'],
      priority: 'High',
      logoUrl: 'https://www.jenkins.io/images/logo-title-opengraph.png'
    },
    {
      name: 'OpenJDK',
      category: 'Programming Language / Runtime',
      description: 'Open-source implementation of Java Platform - required for many DevOps tools',
      requirements: 'Varies by version and platform',
      downloadUrl: 'https://openjdk.org/install/',
      docsUrl: 'https://openjdk.org/install/',
      version: '8, 11, 17, 21 LTS',
      platforms: ['Windows', 'macOS', 'Linux', 'AIX'],
      priority: 'High',
      logoUrl: 'https://openjdk.org/images/openjdk.png'
    },
    {
      name: 'Node.js',
      category: 'Programming Language / Runtime',
      description: 'JavaScript runtime for building scalable network applications. Required for npm, yarn, and most modern front-end build tools.',
      requirements: 'Varies by version and platform, typically 50MB disk space',
      downloadUrl: 'https://nodejs.org/en/download/',
      docsUrl: 'https://nodejs.org/en/docs/',
      version: '18.x, 20.x LTS',
      platforms: ['Windows', 'macOS', 'Linux', 'Source'],
      priority: 'Critical',
      logoUrl: 'https://nodejs.org/static/images/logo.svg'
    },
    {
      name: 'Nginx',
      category: 'Web Server',
      description: 'High-performance HTTP server and reverse proxy - essential for production deployments',
      requirements: 'GCC compiler, PCRE library, zlib library, OpenSSL library',
      downloadUrl: 'https://nginx.org/en/download.html',
      docsUrl: 'https://nginx.org/en/docs/install.html',
      version: 'Stable/Mainline',
      platforms: ['Windows', 'macOS', 'Linux', 'FreeBSD'],
      priority: 'High',
      logoUrl: 'https://nginx.org/nginx.png'
    },
    {
      name: 'Prometheus',
      category: 'Monitoring',
      description: 'Monitoring system and time series database - critical for observability',
      requirements: 'Varies by usage, typically 2GB RAM minimum',
      downloadUrl: 'https://prometheus.io/download/',
      docsUrl: 'https://prometheus.io/docs/prometheus/latest/installation/',
      version: 'Latest',
      platforms: ['Windows', 'macOS', 'Linux', 'FreeBSD'],
      priority: 'High',
      logoUrl: 'https://prometheus.io/assets/prometheus_logo_grey.svg'
    },
    {
      name: 'Ansible',
      category: 'Configuration Management',
      description: 'IT automation platform for configuration management - essential for infrastructure management',
      requirements: 'Python 3.8+, SSH access to target machines',
      downloadUrl: 'https://docs.ansible.com/ansible/latest/installation_guide/index.html',
      docsUrl: 'https://docs.ansible.com/ansible/latest/installation_guide/intro_installation.html',
      version: 'Latest',
      platforms: ['Linux', 'macOS', 'WSL'],
      priority: 'High',
      logoUrl: 'https://www.ansible.com/hubfs/Images/Red_Hat-Ansible_Automation-Logo-RGB.svg'
    },
    {
      name: 'Apache Maven',
      category: 'Build Tool',
      description: 'Software project management and comprehension tool - standard for Java projects',
      requirements: 'Java 8 or higher, JAVA_HOME environment variable',
      downloadUrl: 'https://maven.apache.org/download.cgi',
      docsUrl: 'https://maven.apache.org/install.html',
      version: '3.9.x',
      platforms: ['Windows', 'macOS', 'Linux', 'Unix'],
      priority: 'Medium',
      logoUrl: 'https://maven.apache.org/images/maven-logo-black-on-white.png'
    },
    {
      name: 'Gradle',
      category: 'Build Tool',
      description: 'Open-source build automation tool - modern alternative to Maven',
      requirements: 'Java 8 or higher, JAVA_HOME environment variable',
      downloadUrl: 'https://gradle.org/install/',
      docsUrl: 'https://docs.gradle.org/current/userguide/installation.html',
      version: '8.x',
      platforms: ['Windows', 'macOS', 'Linux', 'Unix'],
      priority: 'Medium',
      logoUrl: 'https://gradle.org/images/gradle-knowledge-graph-logo.png'
    },
    {
      name: 'Apache HTTP Server',
      category: 'Web Server',
      description: 'World\'s most used web server software - alternative to Nginx',
      requirements: 'PCRE library, APR and APR-Util libraries',
      downloadUrl: 'https://httpd.apache.org/download.cgi',
      docsUrl: 'https://httpd.apache.org/docs/2.4/install.html',
      version: '2.4.x',
      platforms: ['Windows', 'macOS', 'Linux', 'Unix'],
      priority: 'Medium',
      logoUrl: 'https://httpd.apache.org/images/httpd_logo_wide_new.png'
    },
    {
      name: 'SonarQube Community Edition',
      category: 'Code Quality',
      description: 'Code quality and security analysis platform - important for code governance',
      requirements: 'Java 11, 2GB RAM minimum, PostgreSQL/MySQL (optional)',
      downloadUrl: 'https://www.sonarsource.com/products/sonarqube/downloads/',
      docsUrl: 'https://docs.sonarqube.org/latest/setup/install-server/',
      version: 'Community Edition',
      platforms: ['Windows', 'macOS', 'Linux'],
      priority: 'Medium',
      logoUrl: 'https://www.sonarsource.com/assets/images/logos/sonarsource.svg'
    },
    {
      name: 'OpenTofu',
      category: 'Infrastructure as Code',
      description: 'Open-source Terraform fork - alternative IaC tool',
      requirements: '64-bit processor, varies by platform',
      downloadUrl: 'https://opentofu.org/docs/intro/install/',
      docsUrl: 'https://opentofu.org/docs/intro/install/',
      version: 'Latest',
      platforms: ['Windows', 'macOS', 'Linux', 'FreeBSD'],
      priority: 'Low',
      logoUrl: 'https://opentofu.org/img/logos/opentofu_logo.svg'
    },
    {
      name: 'JFrog Artifactory OSS',
      category: 'Repository Manager',
      description: 'Universal artifact repository manager - useful for enterprise environments',
      requirements: 'Java 8+, 4GB RAM minimum, 250GB disk space',
      downloadUrl: 'https://jfrog.com/open-source/',
      docsUrl: 'https://www.jfrog.com/confluence/display/JFROG/Installing+Artifactory',
      version: 'OSS Edition',
      platforms: ['Windows', 'macOS', 'Linux', 'Docker'],
      priority: 'Low',
      logoUrl: 'https://jfrog.com/wp-content/uploads/2020/08/jfrog-logo-2020.svg'
    },
    {
      name: 'Rancher Desktop',
      category: 'Container Management',
      description: 'Kubernetes and container management on desktop - alternative to Docker Desktop',
      requirements: '4GB RAM minimum, WSL2 (Windows), admin privileges',
      downloadUrl: 'https://rancherdesktop.io/',
      docsUrl: 'https://docs.rancherdesktop.io/getting-started/installation',
      version: 'Latest',
      platforms: ['Windows', 'macOS', 'Linux'],
      priority: 'Low',
      logoUrl: 'https://rancherdesktop.io/images/rancher-desktop-logo.svg'
    },
    {
      name: 'VirtualBox',
      category: 'Virtualization',
      description: 'Cross-platform virtualization software - less relevant with cloud adoption',
      requirements: '4GB RAM minimum, VT-x/AMD-V support',
      downloadUrl: 'https://www.virtualbox.org/wiki/Downloads',
      docsUrl: 'https://www.virtualbox.org/manual/UserManual.html#installation',
      version: '7.0.x',
      platforms: ['Windows', 'macOS', 'Linux', 'Solaris'],
      priority: 'Low',
      logoUrl: 'https://www.virtualbox.org/graphics/vbox_logo2_gradient.png'
    }
  ];

  const categories = ['All', ...new Set(tools.map(tool => tool.category))];

  const filteredTools = useMemo(() => {
    return tools.filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           tool.category.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || tool.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      <div className={`max-w-7xl mx-auto p-6 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
      <div className="mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              DevOps Tools Documentation
            </h1>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Comprehensive collection of open source DevOps tools ordered by importance for DevOps/Cloud Engineers
            </p>
          </div>
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              isDarkMode 
                ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' 
                : 'bg-white text-gray-600 hover:bg-gray-100 shadow-md'
            }`}
            aria-label="Toggle theme"
          >
            {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-sm">
          <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${
            isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'
          }`}>
            🔥 Critical - Essential daily tools
          </span>
          <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${
            isDarkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-800'
          }`}>
            ⚡ High - Frequently used
          </span>
          <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${
            isDarkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
          }`}>
            📋 Medium - Project specific
          </span>
          <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${
            isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
          }`}>
            💡 Low - Situational use
          </span>
        </div>

        {/* Navigation Links */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <a
            href="/devops-interview-qa"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${
              isDarkMode 
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700' 
                : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600'
            }`}
            style={{ textDecoration: 'none' }}
          >
            📚 DevOps Interview Q&A Guide
          </a>
          <a
            href="/aws-vpc-component"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${
              isDarkMode 
                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700' 
                : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600'
            }`}
            style={{ textDecoration: 'none' }}
          >
            🚀 AWS VPC Practice Visualizer
          </a>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-400'
          }`} />
          <input
            type="text"
            placeholder="Search tools by name, description, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-colors duration-200 text-base ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent' 
                : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            }`}
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className={`px-4 py-3 border rounded-lg transition-colors duration-200 text-base ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent' 
              : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          }`}
        >
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredTools.map((tool, index) => (
          <div key={index} className={`rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700 hover:shadow-gray-900/20' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-20 h-20 rounded-lg flex items-center justify-center p-2 ${
                    isDarkMode ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <img 
                      src={tool.logoUrl} 
                      alt={`${tool.name} logo`}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  <h3 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {tool.name}
                  </h3>
                </div>
                <div className="flex gap-1 flex-col items-end">
                  <span className={`px-3 py-2 text-sm rounded-full font-medium ${
                    tool.priority === 'Critical' 
                      ? isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'
                      : tool.priority === 'High' 
                      ? isDarkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-800'
                      : tool.priority === 'Medium' 
                      ? isDarkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                      : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {tool.priority}
                  </span>
                  <span className={`px-3 py-2 text-sm rounded-full font-medium ${
                    isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {tool.category}
                  </span>
                </div>
              </div>
              
              <p className={`text-base mb-4 line-clamp-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {tool.description}
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                  <div>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Requirements
                    </p>
                    <p className={`text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {tool.requirements}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <FileText className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                  <div>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Version
                    </p>
                    <p className={`text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {tool.version}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-1">
                  {tool.platforms.map((platform, idx) => (
                    <span key={idx} className={`px-3 py-2 text-sm rounded ${
                      isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {platform}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className={`flex gap-2 mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <a
                  href={tool.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white text-base rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
                <a
                  href={tool.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-base rounded-md transition-colors font-medium ${
                    isDarkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-600 text-white hover:bg-gray-700'
                  }`}
                >
                  <ExternalLink className="w-4 h-4" />
                  Docs
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTools.length === 0 && (
        <div className="text-center py-12">
          <div className={`mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <Search className="w-12 h-12 mx-auto" />
          </div>
          <h3 className={`text-xl font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            No tools found
          </h3>
          <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Try adjusting your search terms or category filter
          </p>
        </div>
      )}

      {/* Logo Disclaimer */}
      <div className={`mt-8 p-4 rounded-lg border ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-200'
      }`}>
        <h4 className={`font-semibold mb-2 text-lg ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
          📝 Logo Usage Disclaimer
        </h4>
        <p className={`text-base ${isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>
          All company logos, trademarks, and brand names displayed on this page are the property of their respective owners. 
          These logos are used solely for educational and informational purposes to help identify the corresponding software tools. 
          This usage is intended to fall under fair use provisions. We do not claim any ownership of these logos and 
          respect all intellectual property rights of their respective companies.
        </p>
      </div>

      {/* Footer */}
      <div className={`mt-12 pt-8 border-t text-center text-base ${
        isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
      }`}>
        <p>This documentation includes {tools.length} essential open source DevOps tools ordered by importance for DevOps/Cloud Engineers</p>
        <p className="mt-1">Critical and High priority tools are recommended for immediate adoption in any DevOps environment</p>
        <p className="mt-1">Links and requirements are subject to change. Please verify with official sources.</p>
      </div>
      </div>
    </div>
  );
};

export default DevOpsToolsDocumentation;